import React from "react";
import { Lock, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCachedPixels } from "@/hooks/useCachedPixels";
import { useCachedApps } from "@/hooks/useCachedApps";

interface TrackingData {
  default_pixel_id: string | null;
  default_pixel_name: string | null;
  default_app_id: string | null;
  default_app_name: string | null;
  website_events_enabled: boolean | null;
  app_events_enabled: boolean | null;
  offline_events_enabled: boolean | null;
  crm_events_enabled: boolean | null;
  post_engagements_enabled: boolean | null;
  page_engagements_enabled: boolean | null;
  default_utm_parameters?: string;
}

interface TrackingStepProps {
  data: TrackingData;
  onChange: (data: Partial<TrackingData>) => void;
  adAccountId?: string | null;
}

function LockedSwitch({
  label,
  checked,
  locked,
  lockReason,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  locked: boolean;
  lockReason?: string;
  onCheckedChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-2">
        <span className="text-sm">{label}</span>
        {locked && lockReason && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="text-xs">{lockReason}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} disabled={locked} />
    </div>
  );
}

export function TrackingStep({ data, onChange, adAccountId }: TrackingStepProps) {
  const { data: pixels = [], isLoading: pixelsLoading } = useCachedPixels(adAccountId);
  const { data: apps = [], isLoading: appsLoading } = useCachedApps(adAccountId);

  const pixelSelected = !!data.default_pixel_id;
  const appSelected = !!data.default_app_id;

  const handlePixelChange = (pixelId: string) => {
    if (pixelId === "__none__") {
      onChange({ default_pixel_id: null, default_pixel_name: null });
      return;
    }
    const pixel = pixels.find((p) => p.pixel_id === pixelId);
    onChange({
      default_pixel_id: pixelId,
      default_pixel_name: pixel?.pixel_name ?? null,
      website_events_enabled: true,
    });
  };

  const handleAppChange = (appId: string) => {
    if (appId === "__none__") {
      onChange({ default_app_id: null, default_app_name: null });
      return;
    }
    const app = apps.find((a) => a.app_id === appId);
    onChange({
      default_app_id: appId,
      default_app_name: app?.app_name ?? null,
      app_events_enabled: true,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Tracking</CardTitle>
        <CardDescription>Default tracking configuration for ad sets</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pixel */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Default Dataset (Pixel)</label>
          {pixelsLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading pixels...
            </div>
          ) : (
            <Select value={data.default_pixel_id ?? "__none__"} onValueChange={handlePixelChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a pixel..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {pixels.length === 0 ? (
                  <SelectItem value="__empty__" disabled>No pixels found for this account</SelectItem>
                ) : (
                  pixels.map((p) => (
                    <SelectItem key={p.pixel_id} value={p.pixel_id}>{p.display_name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* App */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Default App</label>
          {appsLoading ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading apps...
            </div>
          ) : (
            <Select value={data.default_app_id ?? "__none__"} onValueChange={handleAppChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select an app..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {apps.length === 0 ? (
                  <SelectItem value="__empty__" disabled>No apps available</SelectItem>
                ) : (
                  apps.map((a) => (
                    <SelectItem key={a.app_id} value={a.app_id}>{a.display_name}</SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Conversion Event Sources */}
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-foreground">Conversion Event Sources</h4>
          <div className="rounded-lg border border-border divide-y divide-border px-4">
            <LockedSwitch
              label="Website Events"
              checked={pixelSelected ? true : !!data.website_events_enabled}
              locked={pixelSelected}
              lockReason="Required when Pixel is selected"
              onCheckedChange={(val) => onChange({ website_events_enabled: val })}
            />
            <LockedSwitch
              label="App Events"
              checked={appSelected ? true : !!data.app_events_enabled}
              locked={appSelected}
              lockReason="Required when App is selected"
              onCheckedChange={(val) => onChange({ app_events_enabled: val })}
            />
            <LockedSwitch
              label="Offline Events"
              checked={!!data.offline_events_enabled}
              locked={false}
              onCheckedChange={(val) => onChange({ offline_events_enabled: val })}
            />
            <LockedSwitch
              label="CRM Events"
              checked={!!data.crm_events_enabled}
              locked={false}
              onCheckedChange={(val) => onChange({ crm_events_enabled: val })}
            />
          </div>
        </div>

        {/* Engagement Event Sources */}
        <div className="space-y-1">
          <h4 className="text-sm font-medium text-foreground">Engagement Event Sources</h4>
          <div className="rounded-lg border border-border divide-y divide-border px-4">
            <LockedSwitch
              label="Post Engagements"
              checked={!!data.post_engagements_enabled}
              locked={false}
              onCheckedChange={(val) => onChange({ post_engagements_enabled: val })}
            />
            <LockedSwitch
              label="Page Engagements"
              checked={!!data.page_engagements_enabled}
              locked={false}
              onCheckedChange={(val) => onChange({ page_engagements_enabled: val })}
            />
          </div>
        </div>

        {/* UTM Parameters */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Default UTM Parameters</label>
          <p className="text-xs text-muted-foreground">
            These UTM parameters will be appended to your ad URLs by default. Example: utm_source=facebook&amp;utm_medium=paid&amp;utm_campaign=summer_sale
          </p>
          <Textarea
            rows={2}
            placeholder="utm_source=facebook&utm_medium=paid&utm_campaign=summer_sale"
            value={data.default_utm_parameters ?? ""}
            onChange={(e) => onChange({ default_utm_parameters: e.target.value })}
          />
        </div>
      </CardContent>
    </Card>
  );
}
